import {
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { SidebarInset } from "../components/ui/sidebar";
import { Button } from "../components/ui/button";
import {
  SettingsScopeProvider,
  useOptionalSettingsScope,
} from "../components/settings/SettingsScopeContext";
import { SETTINGS_DEVICE_ONLY_PATHS } from "../components/settings/SettingsScopeSentence";
import {
  retainSettingsScope,
  validateSettingsRouteSearch,
} from "../components/settings/settingsScopeNavigation";

/**
 * Explains a stale scope selection instead of rendering a page that would
 * silently act on a different target. Three doors stay open: device-only
 * pages ignore the scope entirely, so a stale selection must not block them;
 * while the data both axes resolve against has not settled once, an empty
 * list must not read as "unavailable", so the page passes through; and the
 * explanation carries a reset, because the sentence that would otherwise
 * change the scope hides itself here while the stale axes ride every
 * category navigation.
 */
export function SettingsScopeBoundary({ children }: { readonly children: ReactNode }) {
  const context = useOptionalSettingsScope();
  // No provider means a suite or surface mounted the layout standalone;
  // there is no scope to be stale, so nothing to explain.
  if (context === null) return children;
  if (SETTINGS_DEVICE_ONLY_PATHS.has(context.pathname)) return children;
  if (!context.scopeDataReady) return children;
  if (context.scope.kind !== "unavailable") return children;
  return (
    <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto p-6">
      <div className="flex max-w-md flex-col items-center gap-3 text-center">
        <p className="text-center text-muted-foreground text-sm" role="status">
          {context.scope.message}
        </p>
        <Button
          onClick={() => {
            context.selectScope({});
          }}
          size="sm"
          variant="outline"
        >
          Reset scope
        </Button>
      </div>
    </div>
  );
}

export function SettingsContentLayout() {
  const navigate = useNavigate();
  // Re-keying the content on the scope search remounts the page when the
  // target changes, the same reset upstream performs, so rows that later
  // adopt the scope never render one target's values under another's
  // sentence. The optional read keeps the SSR and standalone suites, which
  // mount this layout without the provider, rendering exactly as before.
  const scope = useOptionalSettingsScope();
  const searchKey = scope === null ? "" : JSON.stringify(scope.search);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        void navigate({ to: "/", replace: true });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [navigate]);

  return (
    <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      {/* No spacer row.
          These two divs reserved a header's worth of height (44px web, 52px
          Electron — the exact height rules `ChatActiveThreadHeader` uses) to
          stand in for the per-surface header settings does not render. The bar
          above already occupies that strip and owns the drag region, and
          `SettingsPageContainer` supplies its own `pt-4 sm:pt-6`, so the
          reservation only pushed every settings page down by a bar height. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
        <div key={searchKey} className="min-h-0 flex flex-1 flex-col">
          <SettingsScopeBoundary>
            <Outlet />
          </SettingsScopeBoundary>
        </div>
      </div>
    </SidebarInset>
  );
}

function SettingsRouteLayout() {
  const rawSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  return (
    <SettingsScopeProvider
      search={rawSearch}
      pathname={pathname}
      onChange={(next) => {
        // Send every axis so the retain middleware sees an explicit target
        // even when the choice is "all", which is the absence of a key.
        void navigate({
          to: pathname,
          search: () => ({
            project: next.project,
            machine: next.machine,
          }),
          hash: "",
          resetScroll: false,
        });
      }}
    >
      <SettingsContentLayout />
    </SettingsScopeProvider>
  );
}

export const Route = createFileRoute("/settings")({
  validateSearch: validateSettingsRouteSearch,
  search: { middlewares: [retainSettingsScope] },
  beforeLoad: async ({ context, location }) => {
    if (context.authGateState.status !== "authenticated") {
      throw redirect({ to: "/login", replace: true });
    }

    if (location.pathname === "/settings") {
      // Not /settings/profile: that path is now a redirect into General, and
      // chaining one redirect into another shows the user two navigations.
      throw redirect({ to: "/settings/general", replace: true });
    }
  },
  component: SettingsRouteLayout,
});
